import type { Reducer } from 'use-immer';
import type {
  SmartBookingCustomerInput,
  SmartBookingCompanyInput,
  SmartBookingCustomerType,
} from '../../../__generated__/globalTypes';
import { ConsultationType } from '../../../__generated__/globalTypes';
import type { appointmentServicesAndBranches_consultationServices } from '../../graphql/Appointment/__generated__/appointmentServicesAndBranches';

export const initialState: {
  customerType?: SmartBookingCustomerType;
  service?: appointmentServicesAndBranches_consultationServices;
  serviceType?: ConsultationType;
  serviceLocationId?: string;
  date?: Date;
  skipLoaction: boolean;
  reserveAppointmentId?: string;
  note?: string;
  customerInfo?: SmartBookingCustomerInput;
  companyInfo?: SmartBookingCompanyInput;
} = {
  service: undefined,
  serviceType: undefined,
  serviceLocationId: undefined,
  date: undefined,
  skipLoaction: false,
  reserveAppointmentId: undefined,
  customerInfo: undefined,
  note: undefined,
};

interface SelectCustomerType {
  type: 'selectCustomerType';
  customerType?: SmartBookingCustomerType;
}

interface SelectService {
  type: 'selectService';
  service?: appointmentServicesAndBranches_consultationServices;
}

interface SelectServiceType {
  type: 'selectServiceType';
  serviceType: ConsultationType;
}

interface SelectServiceLocationId {
  type: 'selectServiceLocationId';
  serviceLocationId: string;
}

interface SelectDateAndTime {
  type: 'selectDateAndTime';
  date?: Date;
}

interface SetReserveAppointmentId {
  type: 'setReserveAppointmentId';
  id: string;
}

interface SetNote {
  type: 'setNote';
  note: string;
}

interface SetCustomerInfo {
  type: 'setCustomerInfo';
  customerInfo: SmartBookingCustomerInput;
}

interface SetCompanyInfo {
  type: 'setCompanyInfo';
  companyInfo: SmartBookingCompanyInput;
}

interface SetClearInfo {
  type: 'setClearInfo';
}

export type ActionType =
  | SelectCustomerType
  | SelectService
  | SelectServiceType
  | SelectServiceLocationId
  | SelectDateAndTime
  | SetReserveAppointmentId
  | SetCustomerInfo
  | SetNote
  | SetCompanyInfo
  | SetClearInfo;

export const AppointmentReducer: Reducer<typeof initialState, ActionType> = (
  draft: typeof initialState,
  action: ActionType,
) => {
  switch (action.type) {
    case 'selectCustomerType': {
      draft.customerType = action.customerType;
      draft.serviceType = undefined;
      draft.serviceLocationId = undefined;
      draft.date = undefined;
      draft.skipLoaction = false;
      draft.service = undefined;
      draft.reserveAppointmentId = undefined;
      return;
    }
    case 'selectService': {
      draft.serviceType = undefined;
      draft.serviceLocationId = undefined;
      draft.date = undefined;
      draft.skipLoaction = false;
      draft.service = action.service;
      draft.reserveAppointmentId = undefined;

      // types is eather phonecall or meeting. if there only one option we auto select it
      if (action.service && action.service.types.length === 1) {
        const serviceType = action.service.types[0];
        draft.serviceType = serviceType.type;
        // if there is only one branch to pick, you guessed it, we auto select it
        if (serviceType.availableServiceLocation.length === 1) {
          draft.serviceLocationId = serviceType.availableServiceLocation[0].id;
          // if there is only one location and this is phone call we hide the locations dropdown
          if (serviceType.type === ConsultationType.Phonecall) {
            draft.skipLoaction = true;
          }
        }
      }
      return;
    }

    case 'selectServiceType': {
      draft.serviceLocationId = undefined;
      draft.date = undefined;
      draft.serviceType = action.serviceType;
      draft.skipLoaction = false;
      draft.reserveAppointmentId = undefined;

      const type = draft.service?.types.find(
        (i) => i.type === action.serviceType,
      );
      if (type && type.availableServiceLocation.length === 1) {
        draft.serviceLocationId = type.availableServiceLocation[0].id;
        // if there is only one location and this is phone call we hide the locations dropdown
        if (
          type.type === ConsultationType.Phonecall ||
          type.type === ConsultationType.Videocall
        ) {
          draft.skipLoaction = true;
        }
      }
      return;
    }

    case 'selectServiceLocationId': {
      draft.date = undefined;
      draft.reserveAppointmentId = undefined;
      draft.serviceLocationId = action.serviceLocationId;
      return;
    }
    case 'selectDateAndTime': {
      draft.reserveAppointmentId = undefined;
      draft.date = action.date;
      return;
    }
    case 'setReserveAppointmentId': {
      draft.reserveAppointmentId = action.id;
      return;
    }
    case 'setCustomerInfo': {
      draft.customerInfo = action.customerInfo;
      return;
    }
    case 'setCompanyInfo': {
      draft.companyInfo = action.companyInfo;
      return;
    }
    case 'setNote': {
      draft.note = action.note;
      return;
    }
    case 'setClearInfo': {
      draft.service = undefined;
      draft.serviceType = undefined;
      draft.serviceLocationId = undefined;
      draft.date = undefined;
      draft.skipLoaction = false;
      draft.reserveAppointmentId = undefined;
      draft.customerInfo = undefined;
      draft.note = undefined;
      return;
    }
    default:
  }
};
